import { useEffect, useState } from "react";
import { motion, animate, useMotionValue, useTransform } from "framer-motion";
import { styled } from "~/ui/style/stitches.config";

interface FlippableCardProps {
  flipped: boolean;
  frontImage: string;
  backImage: string;
  reversed?: boolean;
  width: number;
  height: number;
  children?: React.ReactNode;
  /** Called when flip animation starts */
  onFlipStart?: () => void;
  /** Called when flip animation ends */
  onFlipEnd?: () => void;
  /** If true, card starts flipped without animation */
  initiallyFlipped?: boolean;
}

const FLIP_TIME = 0.45;
const FLIP_DELAY = 0.1;
const LIFT_TIME = 0.9;

/**
 * A card that flips with a lift effect, similar to the tarot room.
 * Uses framer-motion animate() for the flip + lift animation.
 */
export const FlippableCard = ({
  flipped,
  frontImage,
  backImage,
  reversed = false,
  width,
  height,
  onFlipStart,
  onFlipEnd,
  initiallyFlipped = false,
}: FlippableCardProps) => {
  const rotateY = useMotionValue(initiallyFlipped ? 180 : 0);
  const lift = useMotionValue(0);
  const [showFront, setShowFront] = useState(initiallyFlipped);
  const [hasAnimated, setHasAnimated] = useState(initiallyFlipped);

  // Animate flip when flipped prop changes
  useEffect(() => {
    if (flipped && !hasAnimated) {
      onFlipStart?.();
      // Flip from back (0) to front (180)
      animate(rotateY, [0, 180], {
        type: "tween",
        duration: FLIP_TIME,
        ease: "easeIn",
        delay: FLIP_DELAY,
      });
      animate(lift, [0, 1, 0], {
        type: "tween",
        duration: LIFT_TIME,
        ease: "easeOut",
        onComplete: () => onFlipEnd?.(),
      });
      setHasAnimated(true);
    }
  }, [flipped, hasAnimated]);

  // Toggle front/back visibility at 90 degrees
  useEffect(() => {
    const unsubscribe = rotateY.onChange((v) => {
      setShowFront(v > 90);
    });
    return () => unsubscribe();
  }, [rotateY]);

  // Shadow transforms based on lift
  const shadowBlur = useTransform(lift, [0, 1], [2, 10]);
  const shadowY = useTransform(lift, [0, 1], [4, 20]);
  const shadowOpacity = useTransform(lift, [0, 1], [0.25, 0.4]);
  const liftY = useTransform(lift, [0, 1], [0, -30]);

  return (
    <CardContainer style={{ width, height }}>
      {/* Shadow - pushed back in z-space so card is always in front */}
      <motion.div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 12,
          pointerEvents: "none",
          background: "rgba(0,0,0,0.5)",
          filter: useTransform(shadowBlur, (v) => `blur(${v}px)`),
          opacity: shadowOpacity,
          y: shadowY,
          zIndex: -1,
          translateZ: -50,
        }}
      />

      {/* Card */}
      <motion.div
        style={{
          position: "absolute",
          inset: 0,
          transformStyle: "preserve-3d",
          rotateY,
          y: liftY,
        }}
      >
        {/* Back face (card back) - visible when rotateY < 90 */}
        <CardFace
          side="back"
          style={{
            backgroundImage: `url(${backImage})`,
            opacity: showFront ? 0 : 1,
            rotateY: 0,
          }}
        />

        {/* Front face (card art) - visible when rotateY > 90 */}
        <CardFace
          side="front"
          style={{
            backgroundImage: `url(${frontImage})`,
            opacity: showFront ? 1 : 0,
            rotateY: 180,
            rotate: reversed ? 180 : 0,
          }}
        />
      </motion.div>
    </CardContainer>
  );
};

const CardContainer = styled("div", {
  position: "relative",
  transformStyle: "preserve-3d",
  perspective: "600px",
});

const CardFace = styled(motion.div, {
  position: "absolute",
  inset: 0,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundColor: "rgb(230, 230, 230)",
  borderRadius: "8px",
  backfaceVisibility: "hidden",
  border: "1px solid rgba(96, 96, 96, .3)",

  variants: {
    side: {
      back: {
        border: "1px solid rgba(0, 0, 0, 0.9)",
      },
      front: {},
    },
  },
});

